import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Grid,
  Select,
  Checkbox,
  Textarea,
  Icon,
  Button,
  Text,
  FormErrorMessage,
} from "@chakra-ui/react";

import {useContext, useState, useEffect, useMemo, useLayoutEffect} from "react";
import {FaDollarSign} from "react-icons/fa";
import {MdDelete} from "react-icons/md";
import {BsPlusCircleDotted} from "react-icons/bs";
import {insuranceTypes} from "../helpers/insuranceTypes";
import {SearchableSelect} from "./SearchableSelect";
import {getTelusHealthInsurers, crud} from "../../../crudRequests";
import {normalizeDateInput, toInputDateValue} from "../../../utils/dateValidation";

// A policy that ends before it starts cannot be judged by the clinic, so the
// field says so before it is saved.
export function policyEndsBeforeItStarts(policy) {
  const starts = normalizeDateInput(policy?.insuranceStartDate);
  const ends = normalizeDateInput(policy?.insuranceEndDate);
  return Boolean(policy?.insuranceStartDate && policy?.insuranceEndDate && starts.isValid && ends.isValid && ends.canonical < starts.canonical);
}

export default function UnlimitedInsurers({state,patient,setPatient}) {
  const dashState = state;

  const [telusInsurers, setTelusInsurers] = useState([]);
  const [clinicInsurers, setClinicInsurers] = useState(dashState.insurers || []);

  const requiredIndicator = (
    <Text as="span" color="red.500" ml="1">
      *
    </Text>
  );

  const relationshipOptions = [
    {value: "FSTUD", label: "Full Time Student"},
    {value: "PSTUD", label: "Part Time Student"},
    {value: "SPS", label: "Spouse"},
    {value: "DOMPART", label: "Domestic Partner"},
    {value: "CHILD", label: "Child"},
    {value: "HANDIC", label: "Handicapped Dependent"},
  ];

  const insurersMap = useMemo(() => {
    return (clinicInsurers || []).reduce((ac, e) => {
      ac[e.iid] = e;
      return ac;
    }, {});
  }, [clinicInsurers]);

  useEffect(() => {
    const fetchClinicInsurers = async () => {
      try {
        const response = await crud(dashState, [
          {
            db: dashState.db,
            collection: "insurers",
            parameters: [{ oid: dashState.organization?.oid }],
            method: "find",
          },
        ]);
        if (response?.data?.[0]) {
          setClinicInsurers(response.data[0]);
        }
      } catch (error) {
        console.error("Error fetching clinic insurers:", error);
      }
    };

    const fetchTelusInsurers = async () => {
      try {
        const response = await getTelusHealthInsurers(dashState);
        if (response?.data) {
          setTelusInsurers(response.data);
        }
      } catch (error) {
        console.error("Error fetching Telus health insurers:", error);
      }
    };

    if (dashState.jwt) {
      fetchClinicInsurers();
      fetchTelusInsurers();
    }
  }, []);



  const groupedInsurerOptions = useMemo(() => {
    const options = [];

    if (telusInsurers.length > 0) {
      options.push({
        label: "Telus Available Insurers",
        options: telusInsurers.map((insurer) => ({
          value: `${insurer.eClaimsInsurerID}`,
          label: insurer.displayName.english || insurer.displayName.french,
          isTelus: true,
        })),
      });
    }

    if (clinicInsurers && clinicInsurers.length > 0) {
      options.push({
        label: "Clinic Insurers",
        options: clinicInsurers.map((insurer) => ({
          value: insurer.iid,
          label: insurer.name,
          isTelus: false,
        })),
      });
    }

    return options;
  }, [telusInsurers, clinicInsurers]);




  useLayoutEffect(() => {
    setPatient((prev) => {
      try {
        return {
          ...prev,
          tpp: (prev?.tpp || []).map((e) => {
            e.insurerAddress =
              e.insurerAddress || insurersMap[e.insurerId]?.address || "";
            e.attentionTo =
              e.attentionTo ||
              insurersMap[e.insurerId]?.defaultAttentionTo ||
              "";
            return e;
          }),
        };
      } catch (e) {
        console.log(e);
        return prev;
      }
    });
  }, []);

  const [insurers, setInsurers] = useState(patient.tpp || []);
  const [relatedPersonDobErrors, setRelatedPersonDobErrors] = useState({});
  const [relatedPersonDobInputs, setRelatedPersonDobInputs] = useState({});

  useEffect(() => {
    if (Array.isArray(patient.tpp) && patient.tpp.length > 0) {
      setInsurers(patient.tpp);
    } else {
      setInsurers([]);
    }
  }, [patient.tpp]);

  useEffect(() => {
    const initialInputs = {};
    (patient.tpp || []).forEach((entry, idx) => {
      initialInputs[idx] = toInputDateValue(entry?.relatedPerson?.dob);
    });
    setRelatedPersonDobInputs(initialInputs);
    setRelatedPersonDobErrors({});
  }, [patient?.pid]);

  const updateTppAtIndex = (index, newValue) => {
    setPatient({
      ...patient,
      tpp: patient.tpp?.map((item, idx) => {
        if (idx === index) {
          return {...item, ...newValue};
        }
        return item;
      }),
    });
  };

  return (
    <Box textAlign="center">
      <FormControl is="referringClinicianIsGP">
        <Checkbox
          isChecked={Array.isArray(patient.tpp) ? patient.tpp.length > 0 : !!patient.tpp}
          onChange={(e) => {
            setPatient({
              ...patient,
              tpp: e.target.checked ? [{insurerId: ""}] : null,
            });
            setInsurers(e.target.checked ? [{insurerId: ""}] : []);
          }}
          mb="10"
          mt="2"
        >
          <Text fontSize="sm">
            Is the patient insured by a <b>Third Party Payer</b>?
          </Text>
        </Checkbox>
      </FormControl>

      <Box>
        {insurers?.map((insurer, i) => {
          const patientHasId = !!patient?.pid;
          const insurerSelected = !!patient?.tpp?.[i]?.insurerId;
          const isMemberPolicyRequired = patientHasId && insurerSelected;
          const isInsurerInfoMissing = (value) =>
            isMemberPolicyRequired && !String(value || "").trim();

          return (
            <Box key={i}>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={3}
                mt={i !== 0 ? "8" : "0"}
              >
                <Box as="span" w="17.77rem">
                  <SearchableSelect
                    label="Insurer Name"
                    placeholder="Select Insurer"
                    options={groupedInsurerOptions}
                    value={patient?.tpp[i]?.insurerId}
                    onChange={(selectedOption) => {
                      if (selectedOption?.isTelus) {
                        const telusInsurer = telusInsurers.find(
                          (insurer) =>
                            `${
                                insurer.eClaimsInsurerID
                              }` === selectedOption.value
                        );
                        updateTppAtIndex(i, {
                          insurerId: `${telusInsurer.eClaimsInsurerID}`,
                          insurerName:
                            telusInsurer.displayName?.english ||
                            telusInsurer.displayName?.french ||
                            "",
                          insurerOID: telusInsurer.baseOID,
                          carrierID: telusInsurer.carrierID,
                          carrierOID: telusInsurer.carrierOID,
                          isTelus: true,
                        });
                      } else {
                        const clinicInsurer = clinicInsurers?.find(
                          (insurer) => insurer.iid === selectedOption?.value
                        );
                        updateTppAtIndex(i, {
                          insurerId: selectedOption?.value || "",
                          insurerName: clinicInsurer?.name || "",
                          insurerOID: null,
                          carrierID: null,
                          isTelus: false,
                        });
                      }
                    }}
                    isDisabled={!patient.tpp}
                    isRequired={false}
                  />
                </Box>
                <FormControl id="insuranceType" isDisabled={!patient.tpp}>
                  <FormLabel>Insurance Type</FormLabel>
                  <Select
                    size="sm"
                    variant="outline"
                    placeholder="Select Insurance Type"
                    value={patient?.tpp[i]?.insuranceType}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insuranceType: e.target.value});
                    }}
                  >
                    {insuranceTypes.map((type) => (
                      <option value={type}>{type}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  id="insurerNo"
                  isDisabled={!patient.tpp}
                  isRequired={isMemberPolicyRequired}
                  isInvalid={isInsurerInfoMissing(patient?.tpp[i]?.insurerNo)}
                >
                  <FormLabel requiredIndicator={requiredIndicator}>
                    Claim, Employee, or Identification Number
                  </FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder="123456"
                    value={patient?.tpp[i]?.insurerNo}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insurerNo: e.target.value});
                    }}
                  />
                  {isMemberPolicyRequired && (
                    <FormErrorMessage>Required</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  id="policyNo"
                  isDisabled={!patient.tpp}
                  isRequired={isMemberPolicyRequired}
                  isInvalid={isInsurerInfoMissing(patient?.tpp[i]?.policyNo)}
                >
                  <FormLabel requiredIndicator={requiredIndicator}>
                    Policy, Group, or Plan Number
                  </FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder="123456"
                    value={patient?.tpp[i]?.policyNo}
                    onChange={(e) => {
                      updateTppAtIndex(i, {policyNo: e.target.value});
                    }}
                  />
                  {isMemberPolicyRequired && (
                    <FormErrorMessage>Required</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl id="insurancePlanName" isDisabled={!patient.tpp}>
                  <FormLabel>Insurance Plan Name</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder="Plan name"
                    value={patient?.tpp[i]?.insurancePlanName || ""}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insurancePlanName: e.target.value});
                    }}
                  />
                </FormControl>

                <FormControl id="insurerAddress" isDisabled={!patient.tpp}>
                  <FormLabel>Insurer Address</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder={"123 Main St."}
                    value={patient?.tpp[i]?.insurerAddress || ""}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insurerAddress: e.target.value});
                    }}
                  />
                </FormControl>

                <FormControl id="attentionTo" isDisabled={!patient.tpp}>
                  <FormLabel>Attention To</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder={"John Doe"}
                    value={patient?.tpp[i]?.attentionTo || ""}
                    onChange={(e) => {
                      updateTppAtIndex(i, {attentionTo: e.target.value});
                    }}
                  />
                </FormControl>
                <FormControl id="authNo" isDisabled={!patient.tpp}>
                  <FormLabel>Auth Number</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="text"
                    placeholder="123456"
                    value={patient?.tpp[i]?.authNo}
                    onChange={(e) => {
                      updateTppAtIndex(i, {authNo: e.target.value});
                    }}
                  />
                </FormControl>

                <FormControl
                  id="sessionDeductionAmount"
                  isDisabled={!patient.tpp}
                >
                  <FormLabel>
                    Deduction Amount (
                    <Icon as={FaDollarSign} fontSize="15px" mt="-0.5" />)
                  </FormLabel>

                  <Input
                    size="sm"
                    variant="outline"
                    type="number"
                    placeholder="75"
                    value={patient?.tpp[i]?.sessionDeductionAmount}
                    onChange={(e) => {
                      updateTppAtIndex(i, {
                        sessionDeductionAmount: Number(e.target.value),
                      });
                    }}
                  />
                </FormControl>

                <FormControl id="noOfInsuredSessions" isDisabled={!patient.tpp}>
                  <FormLabel># of Insured Sessions</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="number"
                    placeholder="10"
                    value={patient?.tpp[i]?.noOfInsuredSessions}
                    onChange={(e) => {
                      updateTppAtIndex(i, {
                        noOfInsuredSessions: Number(e.target.value),
                      });
                    }}
                  />
                </FormControl>

                <FormControl id="insuranceStartDate" isDisabled={!patient.tpp}>
                  <FormLabel>Active Since</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="date"
                    placeholder="30"
                    value={toInputDateValue(patient?.tpp[i]?.insuranceStartDate)}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insuranceStartDate: e.target.value});
                    }}
                  />
                </FormControl>

                {/* The last day the policy covers; empty means no end. */}
                <FormControl
                  id="insuranceEndDate"
                  isDisabled={!patient.tpp}
                  isInvalid={policyEndsBeforeItStarts(patient?.tpp[i])}
                >
                  <FormLabel>Active Until</FormLabel>
                  <Input
                    size="sm"
                    variant="outline"
                    type="date"
                    value={toInputDateValue(patient?.tpp[i]?.insuranceEndDate)}
                    onChange={(e) => {
                      updateTppAtIndex(i, {insuranceEndDate: e.target.value});
                    }}
                  />
                  <FormErrorMessage>
                    Active Until must be on or after Active Since.
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Related Person Section */}
              <Box mt="6" p="4">
                <FormControl isDisabled={!patient.tpp}>
                  <Checkbox
                    isChecked={patient?.tpp[i]?.hasRelatedPerson || false}
                    onChange={(e) => {
                      updateTppAtIndex(i, {
                        hasRelatedPerson: e.target.checked,
                        relatedPerson: e.target.checked
                          ? {
                              firstName: "",
                              lastName: "",
                              gender: "",
                              dob: "",
                              relationship: "CHILD",
                            }
                          : null,
                      });
                      setRelatedPersonDobInputs((prev) => ({
                        ...prev,
                        [i]: "",
                      }));
                      setRelatedPersonDobErrors((prev) => ({
                        ...prev,
                        [i]: "",
                      }));
                    }}
                    mb="4"
                  >
                    <Text fontSize="sm">Add Related Person Information</Text>
                  </Checkbox>
                </FormControl>

                {patient?.tpp[i]?.hasRelatedPerson && (
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={3}
                  >
                    <FormControl
                      id="relatedPersonFirstName"
                      isDisabled={!patient.tpp}
                    >
                      <FormLabel>First Name</FormLabel>
                      <Input
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        type="text"
                        placeholder="John"
                        value={patient?.tpp[i]?.relatedPerson?.firstName || ""}
                        onChange={(e) => {
                          updateTppAtIndex(i, {
                            relatedPerson: {
                              ...patient?.tpp[i]?.relatedPerson,
                              firstName: e.target.value,
                            },
                          });
                        }}
                      />
                    </FormControl>

                    <FormControl
                      id="relatedPersonLastName"
                      isDisabled={!patient.tpp}
                    >
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        type="text"
                        placeholder="Doe"
                        value={patient?.tpp[i]?.relatedPerson?.lastName || ""}
                        onChange={(e) => {
                          updateTppAtIndex(i, {
                            relatedPerson: {
                              ...patient?.tpp[i]?.relatedPerson,
                              lastName: e.target.value,
                            },
                          });
                        }}
                      />
                    </FormControl>

                    <FormControl
                      id="relatedPersonGender"
                      isDisabled={!patient.tpp}
                    >
                      <FormLabel>Gender</FormLabel>
                      <Select
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        placeholder="Select Gender"
                        value={patient?.tpp[i]?.relatedPerson?.gender || ""}
                        onChange={(e) => {
                          let genderValue = e.target.value;
                          if (e.target.value === "Trans Woman") {
                            genderValue = "Female";
                          } else if (e.target.value === "Trans Man") {
                            genderValue = "Male";
                          }

                          updateTppAtIndex(i, {
                            relatedPerson: {
                              ...patient?.tpp[i]?.relatedPerson,
                              gender: genderValue,
                            },
                          });
                        }}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Trans Man">Trans Man</option>
                        <option value="Trans Woman">Trans Woman</option>
                        <option value="Other">Other</option>
                      </Select>
                    </FormControl>

                    <FormControl
                      id="relatedPersonDob"
                      isDisabled={!patient.tpp}
                      isInvalid={!!relatedPersonDobErrors[i]}
                    >
                      <FormLabel>Date of Birth</FormLabel>
                      <Input
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        type="date"
                        value={relatedPersonDobInputs[i] ?? toInputDateValue(patient?.tpp[i]?.relatedPerson?.dob)}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setRelatedPersonDobInputs((prev) => ({
                            ...prev,
                            [i]: nextValue,
                          }));
                          if (!nextValue) {
                            setRelatedPersonDobErrors(prev => ({...prev, [i]: ""}));
                            updateTppAtIndex(i, {
                              relatedPerson: {
                                ...patient?.tpp[i]?.relatedPerson,
                                dob: "",
                              },
                            });
                            return;
                          }
                          const parsed = normalizeDateInput(nextValue);
                          if (parsed.isValid) {
                            setRelatedPersonDobErrors(prev => ({...prev, [i]: ""}));
                            updateTppAtIndex(i, {
                              relatedPerson: {
                                ...patient?.tpp[i]?.relatedPerson,
                                dob: parsed.canonical,
                              },
                            });
                          }
                        }}
                        onBlur={() => {
                          const currentValue = relatedPersonDobInputs[i] || "";
                          if (!currentValue) {
                            setRelatedPersonDobErrors(prev => ({...prev, [i]: ""}));
                            return;
                          }
                          const parsed = normalizeDateInput(currentValue);
                          setRelatedPersonDobErrors(prev => ({...prev, [i]: parsed.isValid ? "" : parsed.error}));
                        }}
                      />
                      {relatedPersonDobErrors[i] && <FormErrorMessage>{relatedPersonDobErrors[i]}</FormErrorMessage>}
                    </FormControl>

                    <FormControl
                      id="relatedPersonRelationship"
                      isDisabled={!patient.tpp}
                    >
                      <FormLabel>Relationship</FormLabel>
                      <Select
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        value={
                          patient?.tpp[i]?.relatedPerson?.relationship ||
                          "CHILD"
                        }
                        onChange={(e) => {
                          updateTppAtIndex(i, {
                            relatedPerson: {
                              ...patient?.tpp[i]?.relatedPerson,
                              relationship: e.target.value,
                            },
                          });
                        }}
                      >
                        {relationshipOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Box>

              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                }}
                gap={3}
                mt="6"
              >
                <FormControl
                  id="otherInsuranceDetails"
                  isDisabled={!patient.tpp}
                >
                  <FormLabel>Other Insurance Details</FormLabel>
                  <Textarea
                    size="sm"
                    variant="outline"
                    placeholder="Additional details for reference"
                    h="10rem"
                    value={patient?.tpp[i]?.otherInsuranceDetails}
                    onChange={(e) => {
                      updateTppAtIndex(i, {
                        otherInsuranceDetails: e.target.value,
                      });
                    }}
                  ></Textarea>
                </FormControl>
                <FormControl id="invoiceNotes" isDisabled={!patient.tpp}>
                  <FormLabel>Invoice Notes</FormLabel>
                  <Textarea
                    size="sm"
                    variant="outline"
                    placeholder="Any extra information to be included on the invoice"
                    h="10rem"
                    value={patient?.tpp[i]?.invoiceNotes}
                    onChange={(e) => {
                      updateTppAtIndex(i, {invoiceNotes: e.target.value});
                    }}
                  ></Textarea>
                </FormControl>
              </Grid>

              <Button
                mt="4"
                size="sm"
                variant="outline"
                borderColor="gray.300"
                bg="white"
                _hover={{bg: "#f8f9fa"}}
                onClick={() => {
                  setPatient({
                    ...patient,
                    tpp:
                      insurers.length > 1
                        ? patient.tpp.filter((_, idx) => idx !== i)
                        : false,
                  });
                  setInsurers(insurers.filter((_, idx) => idx !== i));
                }}
              >
                <Icon as={MdDelete} mt="-0.5" mr={2} /> Remove Insurer
              </Button>
            </Box>
          );
        })}
      </Box>
      {patient?.tpp && (
        <Icon
          as={BsPlusCircleDotted}
          fontSize={"26px"}
          textAlign={"center"}
          mt="6"
          mb="4"
          cursor="pointer"
          onClick={() => {
            setInsurers([...insurers, {insurerId: ""}]);
            setPatient({
              ...patient,
              tpp: [...patient.tpp, {insurerId: ""}],
            });
          }}
        />
      )}
    </Box>
  );
}
